import { IDialogProps } from 'Components/Core/Dialog/types';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as amddActions } from '../../../../../Data/ReduxViewModels/AssignedValuesTests';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import {
  Button,
  ButtonGroup,
  ConfirmDialog,
  Dialog,
  Field,
  Link,
  Message,
  Spinner,
  TextOutput,
} from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, Table } from '../../../../Layout';
import { AsImportedToRelativeCompactionTable } from './AsImportedToRelativeCompactionTable';

interface IMoreInformationDialog extends IWorksheetComponent, IDialogProps {
  AmddTestId: any;
}

let MoreInformationDialogSection: React.FC<IMoreInformationDialog> = ({
  onClose,
  visible,
  namePrefix,
  createWorksheetSelector,
  AmddTestId: ImportId,
  ...otherProps
}) => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.assignedMaximumDryDensityTests.status.loadingEntities);
  const oversizeSieve = useSelector(createWorksheetSelector!('OversizeSieveApertureSize'), deepEquals);
  /**
   * Retrieve available MDD tests from state.
   */
  const MddReferenceTests: any[] = useSelector(
    pipe((state) => state.assignedMaximumDryDensityTests.sourceEntities, valueOrDefault([])),
    deepEquals
  );
  const AssignedValueTestID = useSelector(createWorksheetSelector!('TestPortionId')) as Guid;

  /**
   * Re-imports Assigned Value Source Entities for an Assigned Value Test.
   * Only imports them if they have a mismatch flag
   */
  const recalculateAssignedValue = () => {
    dispatch(amddActions.getSourceEntities({ request: { assignedValueId: AssignedValueTestID, reimport: true } }));
  };

  const ReimportRequired = MddReferenceTests?.filter((row) => row.hasMismatch).length > 0;
  const NumberOfTimesUsed = useSelector(createWorksheetSelector!('TimesUsed')) as number;

  useEffect(() => {
    if (visible) {
      dispatch(amddActions.getSourceEntities({ request: { assignedValueId: AssignedValueTestID } }));
    } else {
      dispatch(amddActions.cleanupSourceEntities());
    }
  }, [dispatch, visible, onClose, AssignedValueTestID]);

  return (
    <Dialog
      {...otherProps}
      title="Assigned Values"
      visible={visible}
      onClose={onClose}
      width={768}
      buttons={[
        <Button primary key="0" onClick={onClose as any} disabled={loading}>
          Close
        </Button>,
      ]}
    >
      <ListFieldSection>
        <Field
          input={<TextOutput />}
          name="AssignedMaximumDryDensity"
          label="Assigned Maximum Dry Density (t/m³)"
          format={{ uom: 't/m³', nearest: 0.01 }}
        />
        <Field
          input={<TextOutput />}
          name="AssignedOptimumMoistureContent"
          label="Assigned Optimum Moisture Content (%)"
          format={{ nearest: 0.5 }}
        />
        <Field input={<TextOutput />} label="Oversize Sieve Aperture" value={oversizeSieve?.Caption} />
      </ListFieldSection>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0' }}>
        {loading ? (
          <div style={{ position: 'relative', height: '30vh' }}>
            <Spinner large />
          </div>
        ) : (
          <Table
            colWidths={[250, 250, 200, 200, 175, 200, 100]}
            colHeaders={[
              'Sample ID',
              'Material',
              'Maximum Dry Density (t/m³)',
              'Optimum Moisture Content (%)',
              'Oversize (%)',
              'Date Sampled',
            ]}
            data={MddReferenceTests?.map((t: any) => [
              <Link openInNewTab to={`/worksheets/${t?.referenceId}`}>
                {t?.sampleId}
              </Link>,
              <Field input={<TextOutput />} value={t?.material} />,
              <Field
                input={<TextOutput />}
                value={t.maximumDryDensity?.value}
                format={{ uom: 't/m³', nearest: 0.01 }}
              />,
              <Field input={<TextOutput />} value={t?.optimumMoistureContent} format={{ nearest: 0.5 }} />,
              <Field input={<TextOutput />} value={t?.oversizePercent} format={{ nearest: 1 }} />,
              <Field input={<TextOutput />} value={t?.dateSampled} format={{ type: FormatTypes.DateTime }} />,
            ])}
          />
        )}

        {ReimportRequired && (
          <>
            <Message
              warning
              message={'Some previously imported values no longer match values on the source. Re-import recommended.'}
            />

            <ButtonGroup>
              <ConfirmDialog
                confirm
                confirmText={`This action will retrieve the results from selected samples again and update calculated values. Do you want to continue? `}
                confirmButtonText="Confirm"
                confirmTitle="Re-import values"
              >
                <Button primary onClick={() => recalculateAssignedValue()} loading={loading} visible={ReimportRequired}>
                  Re-Import
                </Button>
              </ConfirmDialog>
            </ButtonGroup>
          </>
        )}
      </div>
      <ListFieldSection>
        <Field
          input={<TextOutput />}
          name="DateAssigned"
          label="Date Assigned"
          format={{ type: FormatTypes.DateTime }}
        />
        <Field
          input={<TextOutput />}
          name="TimesUsed"
          label="Number of Times Used"
          additionalInformationDialogContent={<AsImportedToRelativeCompactionTable />}
          additionalInformationDialogTitle="Imported to Relative Compaction Tests"
          additionalInformationDialogVisible={NumberOfTimesUsed > 0 ? true : false}
        />
      </ListFieldSection>
    </Dialog>
  );
};

MoreInformationDialogSection = withInjectedProps(MoreInformationDialogSection);

export { MoreInformationDialogSection };
